import { useI18n } from 'vue-i18n';

/**
 * Composable for validating domain URLs against ankorstore.com domain restrictions
 *
 * @returns Object containing validation functions
 */
export function useDomainValidation() {
  const { t } = useI18n();

  /**
   * Validates if a URL is valid and belongs to ankorstore.com domain or its subdomains
   *
   * @param url - The URL to validate
   * @returns Object containing validation result and error message
   */
  const validateDomain = (url: string): { isValid: boolean; errorMessage: string } => {
    if (!url) {
      return {
        isValid: false,
        errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.error.required')
      };
    }

    if (!url.startsWith('https://')) {
      return {
        isValid: false,
        errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.wrongPrefix')
      };
    }

    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      // Allow ankorstore.com and all its subdomains
      const isAnkorstoreDomain = hostname === 'ankorstore.com' || hostname.endsWith('.ankorstore.com');

      if (!isAnkorstoreDomain) {
        return {
          isValid: false,
          errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.invalidDomain')
        };
      }

      return {
        isValid: true,
        errorMessage: ''
      };
    } catch (e) {
      // Invalid URL format
      return {
        isValid: false,
        errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.invalidDomain')
      };
    }
  };

  /**
   * Validates if a URL has the correct HTTPS prefix
   *
   * @param url - The URL to validate
   * @returns Object containing validation result and error message
   */
  const validateHttpsPrefix = (url: string): { isValid: boolean; errorMessage: string } => {
    if (!url) {
      return {
        isValid: false,
        errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.error.required')
      };
    }

    if (!url.startsWith('https://')) {
      return {
        isValid: false,
        errorMessage: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.wrongPrefix')
      };
    }

    return {
      isValid: true,
      errorMessage: ''
    };
  };

  return {
    validateDomain,
    validateHttpsPrefix
  };
}
